import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import {
  ANY_SPORT_PLAY_SQUAD_CREATED_JOINSECRET,
  ANY_SPORT_PLAY_SQUAD_CREATE_OR_JOIN,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSafePreviousNavigate } from '@sorare/core/src/hooks/useSafePreviousNavigate';

import squadImg from '#src/assets/squad.webp';
import { SquadBackground } from '#src/components/squad/SquadBackground/index.tsx';
import { useCreateSquad } from '#src/hooks/squad/useCreateSquad.ts';

const Root = styled(Vertical)`
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: var(--triple-unit) var(--double-unit);
`;

const SquadLogo = styled.img`
  width: 88px;
  height: auto;
  align-self: center;
`;

const Footer = styled.div`
  position: relative;
  z-index: 1;
  padding: var(--double-unit);
  width: 100%;
`;

export const SquadCreate = () => {
  const [name, setName] = useState('');
  const { createSquad, loading } = useCreateSquad();
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const safeBack = useSafePreviousNavigate(
    generateSportContextPath(ANY_SPORT_PLAY_SQUAD_CREATE_OR_JOIN)
  );
  const { sport } = useSportContext();
  const intl = useIntl();

  const handleCreate = async () => {
    if (!name.trim()) return;

    const result = await createSquad({ name: name.trim(), sport });
    if (result?.createSquad?.currentUser?.squad) {
      navigate(
        generateSportContextPath(ANY_SPORT_PLAY_SQUAD_CREATED_JOINSECRET, {
          params: {
            joinSecret: result.createSquad.currentUser.squad.joinSecret,
          },
        })
      );
    }
  };

  return (
    <Root>
      <SquadBackground />
      <Content gap={3}>
        <Vertical gap={1}>
          <BackButton onBack={safeBack} />
          <BrandM>
            <FormattedMessage
              id="SquadCreate.title"
              defaultMessage="Create a Squad"
            />
          </BrandM>
          <BodyM color="var(--c-nd-700)">
            <FormattedMessage
              id="SquadCreate.subtitle"
              defaultMessage="Every squad starts with one bold choice!"
            />
          </BodyM>
        </Vertical>
        <SquadLogo src={squadImg} alt="" />
        <TextInput
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'SquadCreate.namePlaceholder',
            defaultMessage: 'Squad Name',
          })}
        />
      </Content>
      <Footer>
        <Button
          size="large"
          color="primary"
          onClick={handleCreate}
          disabled={!name.trim() || loading}
          fullWidth
        >
          <FormattedMessage
            id="SquadCreate.cta"
            defaultMessage="Create squad"
          />
        </Button>
      </Footer>
      <HideSeriesBottomNav />
    </Root>
  );
};
